// src/components/WorkoutDisplay.js
import React, { useState, useEffect } from 'react';
import ExerciseItem from './ExerciseItem';
import WorkoutTimer from './WorkoutTimer';
import { saveWorkout } from '../utils/storageUtils';
import { recordWorkoutHistory } from '../utils/historyUtils';
import { bodyAreaOptions } from '../data/options';
import { motion } from 'framer-motion'; // Note: would need to install framer-motion

function WorkoutDisplay({ workout, setWorkout, savedWorkouts, setSavedWorkouts }) {
  const [timer, setTimer] = useState({
    isActive: false,
    isPaused: true,
    time: workout.exercises.length > 0 ? workout.exercises[0].duration * 60 : 0,
    currentExerciseIndex: workout.exercises.length > 0 ? 0 : -1,
  });
  
  const [workoutComplete, setWorkoutComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Check if workout is complete whenever timer changes
  useEffect(() => {
    if (timer.currentExerciseIndex >= workout.exercises.length && timer.currentExerciseIndex !== -1) {
      setWorkoutComplete(true);
      // Record workout in history
      recordWorkoutHistory(workout);
    }
  }, [timer.currentExerciseIndex, workout]);
  
  // Clear save success message after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);
  
  const resetSelections = () => {
    setWorkout(null);
  };
  
  const handleSaveWorkout = async () => {
    setIsSaving(true);
    
    // Add short delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const updatedWorkouts = saveWorkout(workout, savedWorkouts);
    setSavedWorkouts(updatedWorkouts);
    setIsSaving(false);
    setSaveSuccess(true);
  };
  
  const printWorkout = () => {
    setIsPrinting(true);
    
    // Printing logic implementation...
    const printWindow = window.open('', '_blank');
    
    let printContent = `
      <html>
      <head>
        <title>PilatesPlanner Workout</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 20px; line-height: 1.5; color: #333; }
          h1 { color: #2563eb; font-weight: 700; }
          h2 { color: #1f2937; font-weight: 600; margin-top: 1.5rem; }
          h3 { color: #374151; font-weight: 600; }
          .exercise { margin-bottom: 18px; padding-bottom: 18px; border-bottom: 1px solid #e5e7eb; }
          .exercise-name { font-weight: 600; font-size: 18px; color: #1f2937; }
          .exercise-duration { color: #6b7280; font-size: 0.9rem; }
          .exercise-description { margin: 10px 0; color: #4b5563; }
          .tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
          .tag { background: #e6f0ff; padding: 3px 10px; border-radius: 12px; font-size: 12px; color: #2563eb; }
          .section { background: #f0f7ff; padding: 15px; margin: 20px 0; border-radius: 8px; }
          .modifications { background: #fff7ed; padding: 10px; border-radius: 8px; margin-top: 10px; }
          .modifications h4 { color: #b45309; font-size: 14px; margin: 0 0 8px 0; }
          .modifications li { margin-bottom: 4px; color: #92400e; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
          .meta { color: #6b7280; font-size: 0.9rem; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PilatesPlanner Workout</h1>
          <div class="meta">
            <div>Total Duration: ${workout.totalDuration} minutes</div>
            <div>Created: ${new Date().toLocaleDateString()}</div>
          </div>
        </div>
        
        <div class="section">
          <h3>Warm-up (3 minutes)</h3>
          <p>${workout.warmup.description}</p>
        </div>
        
        <h2>Main Exercises</h2>
    `;
    
    workout.exercises.forEach((exercise, index) => {
      printContent += `
        <div class="exercise">
          <div class="exercise-name">${index + 1}. ${exercise.name}</div>
          <div class="exercise-duration">${exercise.duration} min + ${workout.restTime}s rest</div>
          <div class="exercise-description">${exercise.description}</div>
          
          ${exercise.relevantModifications && exercise.relevantModifications.length > 0 ? `
            <div class="modifications">
              <h4>Modifications:</h4>
              <ul>
                ${exercise.relevantModifications.map(mod => `
                  <li><strong>${mod.condition}:</strong> ${mod.instruction}</li>
                `).join('')}
              </ul>
            </div>
          ` : ''}
          
          <div class="tags">
            ${exercise.targetAreas.map(area => {
              const areaOption = bodyAreaOptions.find(option => option.id === area);
              return `<span class="tag">${areaOption ? areaOption.label : area}</span>`;
            }).join('')}
            <span class="tag" style="background: #f3f4f6; color: #4b5563;">${exercise.difficulty.charAt(0).toUpperCase() + exercise.difficulty.slice(1)}</span>
          </div>
        </div>
      `;
    });
    
    printContent += `
        <div class="section">
          <h3>Cool-down (2 minutes)</h3>
          <p>${workout.cooldown.description}</p>
        </div>
        
        <div class="footer">
          <p>Created with PilatesPlanner</p>
          <p>Always consult with a fitness professional before starting a new exercise program.</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      setIsPrinting(false);
    }, 500);
  };
  
  // Determine if workout has modifications for health concerns
  const hasModifications = workout && workout.exercises.some(ex => 
    ex.relevantModifications && ex.relevantModifications.length > 0
  );
  
  // Animation variants for framer-motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-2xl font-bold mb-1">Your Custom Pilates Workout</h2>
            <p className="text-blue-100">Total Duration: {workout.totalDuration} minutes</p>
          </div>
          
          <div className="flex space-x-2 mt-4 md:mt-0">
            <button 
              onClick={handleSaveWorkout}
              disabled={isSaving}
              className={`flex items-center ${
                isSaving ? 'bg-green-300 cursor-not-allowed' : saveSuccess ? 'bg-green-500' : 'bg-green-100 hover:bg-green-200'
              } ${saveSuccess ? 'text-white' : 'text-green-800'} px-3 py-2 rounded-md text-sm transition-colors`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : saveSuccess ? (
                <>
                  <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved!
                </>
              ) : (
                <>
                  <span className="mr-1">💾</span>
                  Save
                </>
              )}
            </button>
            <button 
              onClick={printWorkout}
              disabled={isPrinting}
              className={`flex items-center ${
                isPrinting ? 'bg-purple-300 cursor-not-allowed' : 'bg-purple-100 hover:bg-purple-200'
              } text-purple-800 px-3 py-2 rounded-md text-sm`}
            >
              {isPrinting ? (
                <>
                  <svg className="animate-spin mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Printing...
                </>
              ) : (
                <>
                  <span className="mr-1">🖨️</span>
                  Print
                </>
              )}
            </button>
            <button 
              onClick={resetSelections}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-2 rounded-md text-sm"
            >
              New Workout
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {workoutComplete && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg text-green-800 shadow-sm"
          >
            <div className="flex items-center">
              <span className="text-2xl mr-3">🎉</span>
              <div>
                <h3 className="font-semibold">Workout Complete!</h3>
                <p className="text-sm text-green-700">Great job! This workout has been added to your progress history.</p>
              </div>
            </div>
          </motion.div>
        )}
        
        {hasModifications && (
          <div className="p-3 mb-6 bg-amber-50 text-amber-800 rounded-lg border border-amber-200 text-sm">
            <div className="flex items-start">
              <span className="text-lg mr-2">ℹ️</span>
              <p>
                <strong>Note:</strong> This workout includes modifications for your selected health considerations.
              </p>
            </div>
          </div>
        )}
        
        {workout.message ? (
          <div className="p-4 mb-6 bg-yellow-100 text-yellow-800 rounded-lg">
            {workout.message}
          </div>
        ) : (
          <div className="space-y-6">
            {timer.currentExerciseIndex !== -1 && (
              <WorkoutTimer 
                timer={timer} 
                setTimer={setTimer} 
                workout={workout} 
              />
            )}
            
            <div className="p-5 bg-blue-50 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg text-blue-800 flex items-center">
                <span className="mr-2">🔥</span>
                {workout.warmup.name} (3 minutes)
              </h3>
              <p className="text-gray-700 mt-2">{workout.warmup.description}</p>
            </div>
            
            <motion.div 
              className="space-y-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {workout.exercises.map((exercise, index) => (
                <motion.div key={`${exercise.id}-${index}`} variants={itemVariants}>
                  <ExerciseItem 
                    exercise={exercise} 
                    index={index}
                    currentIndex={timer.currentExerciseIndex}
                    restTime={workout.restTime}
                  />
                </motion.div>
              ))}
            </motion.div>
            
            <div className="p-5 bg-blue-50 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg text-blue-800 flex items-center">
                <span className="mr-2">🧘‍♀️</span>
                {workout.cooldown.name} (2 minutes)
              </h3>
              <p className="text-gray-700 mt-2">{workout.cooldown.description}</p>
            </div>
          </div>
        )}
      </div>
      
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-center">
        <button 
          onClick={resetSelections}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-md shadow-sm transition-colors"
        >
          Create Another Workout
        </button>
      </div>
    </div>
  );
}

export default WorkoutDisplay;